import { Splide } from '@splidejs/splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';

/**
 * KVデフォルト：Splide 初期化処理
 * @param {string} selector - Splideを初期化する要素のセレクタ
 * @param {Object} options - Splideのオプション設定
 * @param {Object} extensions - Splideの拡張機能（必要に応じて）
 */
const initializeKvDefaultSplide = () => {
  const defaultSelector = '#visual .js-splide-fade .splide';
  if (!document.querySelector(defaultSelector)) return;

  const defaultOptions = {
    lazyLoad: 'nearby',
    type: 'fade',
    rewind: true,
    autoplay: true,
    pauseOnHover: false,
    pauseOnFocus: false,
    arrows: false,
    interval: 7000, // 1枚の表示時間
    speed: 2000, // フェード時間
  };

  const splide = new Splide(defaultSelector, defaultOptions);

  // (省略) aria-role 削除の監視はそのまま…

  // ヘルパー（ズーム再始動）
  const restartZoom = (el, durationMs) => {
    if (!el) return;
    el.style.transition = 'none';
    el.style.transform = 'scale(1)';
    // reflow
    el.offsetHeight;
    el.style.transition = `transform ${durationMs}ms linear`;
    el.style.transform = 'scale(1.08)'; // お好みで 1.04〜1.12
  };

  const resetZoom = (el) => {
    if (!el) return;
    el.style.transition = 'none';
    el.style.transform = 'scale(1)';
  };

  const ZOOM_VISIBLE_TIME = defaultOptions.interval;
  const ZOOM_DURATION = Math.max(800, ZOOM_VISIBLE_TIME - 150); // 切替直前に暴れない長さ
  const FADE_MS = defaultOptions.speed;

  // 初期：mountedで現アクティブにズーム開始
  splide.on('mounted', () => {
    const activeZoom = document.querySelector(
      `${defaultSelector} .splide__slide.is-active .kv-zoom`
    );
    if (activeZoom && activeZoom.tagName === 'IMG' && !activeZoom.complete) {
      activeZoom.addEventListener('load', () => restartZoom(activeZoom, ZOOM_DURATION), {
        once: true,
      });
    } else {
      restartZoom(activeZoom, ZOOM_DURATION);
    }
  });

  // 新アクティブにズーム開始（フェードのスタート時点）
  splide.on('active', (Slide) => {
    const el = Slide.slide.querySelector('.kv-zoom');
    if (el && el.tagName === 'IMG' && !el.complete) {
      el.addEventListener('load', () => restartZoom(el, ZOOM_DURATION), { once: true });
    } else {
      restartZoom(el, ZOOM_DURATION);
    }
  });

  // 旧スライドのスケールリセットは“フェード完了後”に遅延して行う
  splide.on('move', (_newIndex, oldIndex) => {
    // フェードが終わるまで待ってからリセット（＝一瞬のscale(1)露出を防ぐ）
    const prevSlide = splide.Components.Slides.getAt(oldIndex)?.slide;
    const prevZoom = prevSlide?.querySelector('.kv-zoom');
    if (prevZoom) {
      setTimeout(() => resetZoom(prevZoom), FADE_MS + 50);
    }
  });

  // 注意：inactiveで即resetしない（チラつきの原因）
  // splide.on('inactive', ...) は使わない

  splide.mount();
};

/**
 * KVナビゲーション付き：Splide 初期化処理（メイン＆サムネイル）
 */
const initializeKvMainAndThumbSliders = () => {
  const thumbSelector = '#visual .js-splide-navigation .splide';
  const thumbOptions = {
    mediaQuery: 'min',
    lazyLoad: 'nearby',
    gap: 0,
    arrows: false,
    drag: false,
    pagination: false,
    isNavigation: true,
    direction: 'ttb',
    heightRatio: 1,
    perPage: 4,
    breakpoints: {
      600: {
        gap: 8,
        heightRatio: 1.2,
      },
      991: {
        gap: 8,
        heightRatio: 1.2,
      },
      1200: {
        direction: 'ltr',
        fixedHeight: '200px',
        gap: 16,
      },
    },
  };

  const thumbSplide = new Splide(thumbSelector, thumbOptions);

  // 初期化後に不要な role を削除
  const observer = new MutationObserver(() => {
    const splideList = document.querySelector(`${thumbSelector} .splide__list`);
    if (splideList && splideList.hasAttribute('aria-orientation')) {
      splideList.removeAttribute('aria-orientation');
      splideList.removeAttribute('role');
    }
  });

  thumbSplide.on('mounted', () => {
    const splideList = document.querySelector(`${thumbSelector} .splide__list`);
    if (splideList) {
      observer.observe(splideList, { attributes: true });
    }
  });

  const mainSelector = '#visual .splide-main';
  const mainOptions = {
    lazyLoad: 'nearby',
    type: 'loop',
    autoplay: true,
    updateOnMove: true,
    speed: 2000,
    pagination: false,
    arrows: false,
    drag: false,
    height: 800,
    breakpoints: {
      1024: {
        height: 'clamp(400px,70vh, 600px)',
      },
    },
  };

  const mainSplide = new Splide(mainSelector, mainOptions);

  // サムネイルとメインを同期
  mainSplide.sync(thumbSplide);

  // Splide インスタンスをマウント
  mainSplide.mount();
  thumbSplide.mount();
};

/**
 * ティッカースライダーの初期化
 */
const initializeTickerSlider = () => {
  const tickerSelector = '.js-ticker .splide';

  if (document.querySelector(tickerSelector)) {
    const tickerOptions = {
      lazyLoad: 'nearby',
      mediaQuery: 'min',
      fixedWidth: '15rem',
      gap: 16,
      type: 'loop',
      arrows: false,
      drag: 'free',
      flickPower: 300,
      pagination: false,
      autoScroll: {
        speed: 0.5,
        pauseOnHover: false,
        pauseOnFocus: false,
      },
      breakpoints: {
        1025: {
          gap: 32,
          fixedWidth: '25rem',
        },
      },
    };

    const tickerSplide = new Splide(tickerSelector, tickerOptions);

    // 初期化後に不要な role を削除
    const observer = new MutationObserver(() => {
      const splideList = document.querySelector(`${tickerSelector} .splide__list`);
      if (splideList && splideList.hasAttribute('aria-roledescription')) {
        splideList.removeAttribute('aria-roledescription');
        splideList.removeAttribute('role');
      }
    });

    tickerSplide.on('mounted', () => {
      const splideList = document.querySelector(`${tickerSelector} .splide__list`);
      if (splideList) {
        observer.observe(splideList, { attributes: true });
      }
    });

    // Splide Extensions を使用してマウント
    tickerSplide.mount({ AutoScroll });
  }
};

/**
 * 通常スライダーの初期化
 */
const initializeSlider = () => {
  const sliderSelector = '.js-slider .splide';

  if (document.querySelector(sliderSelector)) {
    const sliderOptions = {
      type: 'fade',
      rewind: true,
    };

    const sliderSplide = new Splide(sliderSelector, sliderOptions);

    // 初期化後に不要な role を削除
    const observer = new MutationObserver(() => {
      const splideList = document.querySelector(`${sliderSelector} .splide__list`);
      if (splideList && splideList.hasAttribute('aria-roledescription')) {
        splideList.removeAttribute('aria-roledescription');
        splideList.removeAttribute('role');
      }
    });

    sliderSplide.on('mounted', () => {
      const splideList = document.querySelector(`${sliderSelector} .splide__list`);
      if (splideList) {
        observer.observe(splideList, { attributes: true });
      }
    });

    // Splide Extensions を使用してマウント
    sliderSplide.mount();
  }
};

/**
 * Splide 全体の初期化
 */
const initializeSplide = () => {
  // TOPページのみで初期化
  if (window.location.pathname === '/') {
    initializeKvDefaultSplide();
  }
  initializeTickerSlider();
  initializeSlider();
};

/**
 * モジュールの初期化
 */
const init = () => {
  initializeSplide();
};

export default { init };
